import type { ArtistOnboarding, OnboardingFlash } from '@/types/models'

export interface ValidationError {
  field: string
  message: string
}

export interface StepValidationResult {
  isValid: boolean
  errors: ValidationError[]
}

/**
 * Validates Step 1: Profile Information (including account fields)
 * This is used for the old flow and publish validation
 */
export function validateStep1(stepData: any, userAccountExists: boolean | null, skipPasswordValidation = false): StepValidationResult {
  const errors: ValidationError[] = []

  // Check first name
  if (!stepData?.firstName?.trim()) {
    errors.push({
      field: 'First Name',
      message: 'Please enter your first name'
    })
  }

  // Check last name
  if (!stepData?.lastName?.trim()) {
    errors.push({
      field: 'Last Name',
      message: 'Please enter your last name'
    })
  }

  // Check email
  if (!stepData?.email?.trim()) {
    errors.push({
      field: 'Email Address',
      message: 'Please enter your email address'
    })
  } else if (!stepData.email.includes('@') || !stepData.email.includes('.')) {
    errors.push({
      field: 'Email Address',
      message: 'Please enter a valid email address'
    })
  }

  // Skip password validation if admin is viewing someone else's onboarding
  if (!skipPasswordValidation) {
    // Check password
    if (!stepData?.password?.trim()) {
      errors.push({
        field: 'Password',
        message: userAccountExists ? 'Please enter your existing account password' : 'Please choose a password (minimum 6 characters)'
      })
    } else if (!userAccountExists && stepData.password.length < 6) {
      errors.push({
        field: 'Password',
        message: 'Password must be at least 6 characters long'
      })
    }

    // Check password confirmation for new accounts
    if (userAccountExists === false) {
      if (!stepData?.confirmPassword?.trim()) {
        errors.push({
          field: 'Confirm Password',
          message: 'Please confirm your password'
        })
      } else if (stepData.password !== stepData.confirmPassword) {
        errors.push({
          field: 'Confirm Password',
          message: 'Passwords do not match'
        })
      }
    }
  }

  // Check bio
  if (!stepData?.bio?.trim()) {
    errors.push({
      field: 'Description',
      message: 'Please add a description about your tattoo style and experience'
    })
  }

  // Check location
  if (!stepData?.location?.trim()) {
    errors.push({
      field: 'Studio Location',
      message: 'Please enter your studio location'
    })
  }

  // Check coordinates
  if (!stepData?.lat || !stepData?.lon) {
    errors.push({
      field: 'Coordinates',
      message: 'Please generate coordinates by clicking the "Generate Coordinates" button'
    })
  }

  // Check profile image
  if (!stepData?.profileImageUrl?.trim()) {
    errors.push({
      field: 'Profile Image',
      message: 'Please upload a profile image'
    })
  }

  // Check banner image
  if (!stepData?.backdropUrl?.trim()) {
    errors.push({
      field: 'Banner Image',
      message: 'Please upload a banner image'
    })
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validates Profile Information only (without email/password)
 * This is for Step 2 in the new onboarding flow where account is in Step 1
 */
export function validateProfile(stepData: any): StepValidationResult {
  const errors: ValidationError[] = []

  // Check first name
  if (!stepData?.firstName?.trim()) {
    errors.push({
      field: 'First Name',
      message: 'Please enter your first name'
    })
  }

  // Check last name
  if (!stepData?.lastName?.trim()) {
    errors.push({
      field: 'Last Name',
      message: 'Please enter your last name'
    })
  }

  // Check bio
  if (!stepData?.bio?.trim()) {
    errors.push({
      field: 'Description',
      message: 'Please add a description about your tattoo style and experience'
    })
  }

  // Check location
  if (!stepData?.location?.trim()) {
    errors.push({
      field: 'Studio Location',
      message: 'Please enter your studio location'
    })
  }

  // Check coordinates
  if (!stepData?.lat || !stepData?.lon) {
    errors.push({
      field: 'Coordinates',
      message: 'Please generate coordinates by clicking the "Generate Coordinates" button'
    })
  }

  // Check profile image
  if (!stepData?.profileImageUrl?.trim()) {
    errors.push({
      field: 'Profile Image',
      message: 'Please upload a profile image'
    })
  }

  // Check banner image
  if (!stepData?.backdropUrl?.trim()) {
    errors.push({
      field: 'Banner Image',
      message: 'Please upload a banner image'
    })
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validates Step 2: Portfolio Images
 */
export function validateStep2(stepData: any): StepValidationResult {
  const errors: ValidationError[] = []

  const tattooImageCount = stepData?.tattooImageUrls?.length || 0

  if (tattooImageCount < 10) {
    errors.push({
      field: 'Portfolio Images',
      message: `You need at least 10 images, but currently have ${tattooImageCount}. Please add ${10 - tattooImageCount} more.`
    })
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validates Step 3: Flash Tattoos
 */
export function validateStep3(stepData: any, onboarding?: ArtistOnboarding): StepValidationResult {
  const errors: ValidationError[] = []

  // Prioritize stepData (current local state) over onboarding (might be stale from database)
  // Only fall back to onboarding if stepData doesn't have flashes
  const flashes: OnboardingFlash[] = stepData?.flashes || onboarding?.flashes || []

  // Flashes are optional - if user hasn't uploaded any, skip validation
  if (flashes.length === 0) {
    return { isValid: true, errors }
  }

  // Check each flash
  flashes.forEach((flash, index) => {
    const flashNumber = index + 1

    // Check if image is uploaded
    if (!flash.imageUrl) {
      errors.push({
        field: `Flash #${flashNumber} - Image`,
        message: 'Image is still uploading or missing'
      })
    }

    // Check crop validation
    if (!flash.cropValidation?.analyzed) {
      errors.push({
        field: `Flash #${flashNumber} - Crop Analysis`,
        message: 'Crop quality has not been analyzed yet. Please wait for analysis to complete.'
      })
    } else if (!flash.cropValidation?.isValid) {
      const cropError = flash.cropValidation?.error || 'Crop does not meet quality standards'
      errors.push({
        field: `Flash #${flashNumber} - Crop Quality`,
        message: `${cropError}. Please upload a better cropped image with at least 12% padding around the design.`
      })
    }

    // Check pricing
    if (!flash.minPrice?.trim()) {
      errors.push({
        field: `Flash #${flashNumber} - Minimum Price`,
        message: 'Please set a minimum price (e.g., 150€)'
      })
    }

    // Check size
    if (!flash.minSize?.trim()) {
      errors.push({
        field: `Flash #${flashNumber} - Minimum Size`,
        message: 'Please set a minimum size (e.g., 5cm)'
      })
    }
  })

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validates Step 4: Agreement
 */
export function validateStep4(stepData: any): StepValidationResult {
  const errors: ValidationError[] = []

  if (!stepData?.agreementAccepted) {
    errors.push({
      field: 'Artist Agreement',
      message: 'Please read and accept the Artist Agreement by checking the box at the bottom'
    })
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Validates all requirements before publishing
 */
export function validatePublish(
  stepData: any,
  onboarding?: ArtistOnboarding,
  userAccountExists?: boolean | null,
  skipPasswordValidation = false
): StepValidationResult {
  const allErrors: ValidationError[] = []

  // Validate all steps
  const step1Result = validateStep1(stepData[1] || {}, userAccountExists || null, skipPasswordValidation)
  const step2Result = validateStep2(stepData[2] || {})
  const step3Result = validateStep3(stepData[3] || {}, onboarding)
  const step4Result = validateStep4(stepData[4] || {})

  allErrors.push(...step1Result.errors)
  allErrors.push(...step2Result.errors)
  allErrors.push(...step3Result.errors)
  allErrors.push(...step4Result.errors)

  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  }
}

